// Focusing a field while the page is still settling (iPad, right after login).
//
// iOS Safari draws the caret where the field was when it got focus, and doesn't move it when the field
// moves afterwards without being touched — e.g. when the Home zoom (a CSS transform) or the header layout
// is applied a moment later, or when the login page's keyboard closes and the viewport scrolls back. The
// caret then blinks below or beside the search field. So: focus only once layout has had two frames to
// settle, then keep watching the field for a short while, and whenever it has moved, focus it again (which
// makes iOS redraw the caret in the right place). Watching stops as soon as the user interacts.

const WATCH_MS = 4000;
const SETTLE_MS = 120;

function sameRect(a: DOMRect, b: DOMRect): boolean {
  return Math.abs(a.left - b.left) < 0.5 && Math.abs(a.top - b.top) < 0.5 && Math.abs(a.width - b.width) < 0.5 && Math.abs(a.height - b.height) < 0.5;
}

function refocus(el: HTMLInputElement): void {
  const { selectionStart, selectionEnd } = el;
  el.blur();
  el.focus({ preventScroll: true });
  try {
    if (selectionStart !== null && selectionEnd !== null) el.setSelectionRange(selectionStart, selectionEnd);
  } catch {
    // Not a text field with a selection — nothing to restore.
  }
}

/**
 * Focuses the field after layout has settled and keeps its caret on it while the page finishes moving.
 * `getField` is asked again on each step, so the field shown at that moment is used.
 * Returns a function that stops watching.
 */
export function focusFieldWhenSettled(getField: () => HTMLInputElement | null | undefined, watchMs = WATCH_MS): () => void {
  let stopped = false;
  let frame = 0;
  let settleTimer: ReturnType<typeof setTimeout> | undefined;
  let lastRect: DOMRect | null = null;
  let field: HTMLInputElement | null = null;
  const startedAt = performance.now();

  const onUserAction = () => stop();

  const stop = () => {
    if (stopped) return;
    stopped = true;
    cancelAnimationFrame(frame);
    clearTimeout(settleTimer);
    document.removeEventListener('pointerdown', onUserAction, true);
    document.removeEventListener('keydown', onUserAction, true);
    window.visualViewport?.removeEventListener('resize', onViewportChange);
    window.visualViewport?.removeEventListener('scroll', onViewportChange);
  };

  const onViewportChange = () => {
    // The visual viewport moved (keyboard closed / page scrolled back): check the field on the next frame.
    lastRect = null;
  };

  const watch = () => {
    if (stopped) return;
    if (performance.now() - startedAt > watchMs) {
      stop();
      return;
    }
    if (!field || document.activeElement !== field) {
      // Focus went elsewhere (by the app or the user): leave it there.
      stop();
      return;
    }
    const rect = field.getBoundingClientRect();
    if (!lastRect || !sameRect(rect, lastRect)) {
      // Moved (or the viewport did): once it stops moving, put the caret back on it.
      clearTimeout(settleTimer);
      const moved = field;
      settleTimer = setTimeout(() => {
        if (!stopped && document.activeElement === moved) refocus(moved);
      }, SETTLE_MS);
    }
    lastRect = rect;
    frame = requestAnimationFrame(watch);
  };

  // Two frames: the first lets Angular/CSS apply, the second is after that layout has been painted.
  frame = requestAnimationFrame(() => {
    frame = requestAnimationFrame(() => {
      if (stopped) return;
      field = getField() ?? null;
      if (!field) {
        stop();
        return;
      }
      field.focus({ preventScroll: true });
      lastRect = field.getBoundingClientRect();
      document.addEventListener('pointerdown', onUserAction, true);
      document.addEventListener('keydown', onUserAction, true);
      window.visualViewport?.addEventListener('resize', onViewportChange);
      window.visualViewport?.addEventListener('scroll', onViewportChange);
      frame = requestAnimationFrame(watch);
    });
  });

  return stop;
}
